import { CheckCircleOutlined, CloseOutlined, PlusOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Flex, Image, Input, message, Popconfirm, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ConfigContext } from '../../contexts/ConfigContext';
import { GenerateODataFilter } from '../../helpers/odataFunctions';
import { Filter, FilterGroup } from '../../types/types';
import { MetadataSelector } from '../Inputs/MetadataSelector';
import { useUiMetaDataServiceGetBackendv2UimetadataBackendlist, useUiMetaDataServiceGetBackendv2UimetadataBackendlistKey, useWhiteModelServiceGetBackendv2Whitemodel } from '../../backendapi/queries';
import { BomModelService, BomModelViewDTO, WhiteModelViewDTO } from '../../backendapi/requests';
import { BASE_PATH } from '../../Environment';
import { Link } from 'react-router-dom';
import EditBomModel from '../bomModel/EditBomModel';
import CreateBomModel from '../bomModel/CreateBomModel';
import MetadataTag from '../layout/elements/MetadataTag';


const WhiteModelList: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const { currentClient } = useContext(ConfigContext);


    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const [customFilters, setCustomFilters] = useState<Filter[]>([]);
    const [selectedModel, setSelectedModel] = useState<number | undefined>(undefined);
    const [editorOpen, setEditorOpen] = useState(false);
    const [creatorOpen, setCreatorOpen] = useState(false);

    const { data: uiMetaData, isFetched } = useUiMetaDataServiceGetBackendv2UimetadataBackendlist({ clientId: currentClient.id }, [useUiMetaDataServiceGetBackendv2UimetadataBackendlistKey, currentClient.id], { refetchOnWindowFocus: false, enabled: currentClient.id > 0 })
    const metadataKeys = useMemo(() => {
        return uiMetaData?.map(x => x.fieldName) ?? []
    },[currentClient, isFetched]);

    const filters = useMemo(() => {
        let filters: (Filter | FilterGroup)[] = [];

        if (debouncedQuery) {
            filters.push({
                type: 'group', filters: [
                    { type: 'search', values: [{ value: debouncedQuery }], property: 'title', name: 'title' },
                    { type: 'search', values: [{ value: debouncedQuery }], property: uiMetaData?.map(e => 'bomModels[].metadata.' + e.fieldName).join(',') ?? '', name: 'metadata' }
                ], join: 'or'
            });
        }

        for (let i = 0; i < customFilters.length; i++) {
            if(customFilters[i].values.length > 0){
                filters.push({...customFilters[i], property: 'bomModels[].' + customFilters[i].property});
            }
        }

        return filters;
    }, [debouncedQuery, currentClient, customFilters, metadataKeys]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQuery, currentClient, customFilters]);


    const { data, refetch, isFetching } = useWhiteModelServiceGetBackendv2Whitemodel({
        clientId: currentClient.id,  
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { refetchOnWindowFocus: false, placeholderData: (prev) => prev  });

    const handleCustomFilterAdd = useCallback(() => {
        setCustomFilters(v => [...v, { type: 'select', values: [], property: '', name: '' }]);
    }, []);
    const handleCustomFilterRemove = useCallback((index: number) => {
        setCustomFilters(v => v.filter((e,i) => i !== index));
    }, []);
    const handleCustomFilterChange = useCallback((value: Filter, index: number) => {
        setCustomFilters(v => v.map((e,i) => i === index ? value : e));
    }, []);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<WhiteModelViewDTO> | SorterResult<WhiteModelViewDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);
    
    const handleDelete = useCallback((model: BomModelViewDTO) => {
        if (model.id) {
            BomModelService.deleteBackendv2Bommodel( model.id).then(() => {
                messageApi.success("Model deleted");
                refetch();
            });
        }
    }, [messageApi, refetch]);

    const handleEnable = useCallback((model: BomModelViewDTO) => {
        if (model.id) {
            BomModelService.getBackendv2Bommodel1(model.id).then(bom => {
                BomModelService.putBackendv2Bommodel(model.id, {...bom, isEnabled: true}).then(() => refetch());
            });
        }
    }, [messageApi, refetch]);

    const handleDisable = useCallback((model: BomModelViewDTO) => {
        if (model.id) {
            BomModelService.getBackendv2Bommodel1(model.id).then(bom => {
                BomModelService.putBackendv2Bommodel(model.id, {...bom, isEnabled: false}).then(() => refetch());
            });
        }
    }, [messageApi, refetch]);
    
    const handleEdit = useCallback((model: number) => {
        setSelectedModel(model);
        setEditorOpen(true);
    }, []);
    
    const handleEditClose = useCallback(() => {
        setSelectedModel(undefined);
        setEditorOpen(false);
        refetch();
    }, [refetch]);
    
    const handleCreateClose = useCallback(() => {
        setCreatorOpen(false);
        refetch();
    }, [refetch]);

    const columns: ColumnsType<WhiteModelViewDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Thumbnail',
            dataIndex: 'thumbnail',
            key: 'title',
            render: (_, value) => (
                <Image
                    preview={{src: BASE_PATH + value.image}}
                    src={BASE_PATH + value.thumbnail}
                    height={50}
                />)
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
        },
        {
            title: 'Variants',
            dataIndex: 'bomModels',
            key: 'bomModels',
            render: (_, value) => `${value.bomModels.length} variants`
        },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (_, value) => (value.isEnabled ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />)
        },
        {
            title: 'Metadata',
            dataIndex: 'metadata',
            key: 'metadata',
            width: 200,
            render: (_, { metadata }) => (metadata?.filter(e => metadataKeys.includes(e.name.toLowerCase())).map(e => (<MetadataTag key={e.name + e.value} name={e.name.toLowerCase()} label={e.value.toLowerCase()}/>)))
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Link to={`/bommodels/${record.id}`}>Edit variations</Link>
                </Space>
            ),
        },
    ];

    const subColumns: ColumnsType<BomModelViewDTO> = [
        {
            title: 'Thumbnail',
            dataIndex: 'thumbnail',
            key: 'title',
            render: (_, value) => (
                <Image
                    src={BASE_PATH + value.thumbnail}
                    preview={{src: BASE_PATH + value.image}}
                    height={50}
                />)
        },
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
        },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (_, value) => (value.isEnabled ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />)
        },
        {
            title: 'Metadata',
            dataIndex: 'metadata',
            key: 'metadata',
            width: 200,
            render: (_, { metadata }) => (metadata?.filter(e => metadataKeys.includes(e.name.toLowerCase())).map(e => (<MetadataTag key={e.name + e.value} name={e.name.toLowerCase()} label={e.value.toLowerCase()}/>)))
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => handleEdit(record.id)}>Edit</Button>
                    {record.isEnabled ?
                        <Button type="link" onClick={() => handleDisable(record)}>Disable</Button> :
                        <Button type="link" onClick={() => handleEnable(record)}>Enable</Button>
                    }
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}
            <Flex justify='space-between' style={{ marginBottom: 16 }}>
                <Space>
                    <Input prefix={<SearchOutlined />} placeholder='Search' value={query} onChange={e => setQuery(e.target.value)} />
                </Space>

                <Space>
                    <Button type="primary" onClick={() => setCreatorOpen(true)} icon={<PlusOutlined />}>
                        Create models
                    </Button>
                </Space>
            </Flex>

            <Space orientation='vertical' style={{ marginBottom: 16}}>
                {customFilters.map((e,i) => <Space key={e.property}><MetadataSelector assetType='bommodel' value={e} onChange={v => handleCustomFilterChange(v, i)} /><Button onClick={() => handleCustomFilterRemove(i)} icon={<CloseOutlined />}></Button></Space>)}
                <Button onClick={handleCustomFilterAdd} icon={<PlusOutlined />}>Filter</Button>
            </Space>

            <EditBomModel isOpen={editorOpen} onClose={handleEditClose} modelId={selectedModel} />
            <CreateBomModel isOpen={creatorOpen} onCancel={handleCreateClose} onCreated={handleCreateClose} />

            <Table
                loading={isFetching}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                expandable={{
                    expandedRowRender: (record) => <Table rowKey={"id"} dataSource={record.bomModels} columns={subColumns}></Table>,
                    rowExpandable: () => true,
                }}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showQuickJumper: true,
                    showSizeChanger: true,
                }} />
        </>
    );
};

export default WhiteModelList;